import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import {
  DashboardListItemViewFragment,
  DashboardUseCase,
  useListDashboardsQuery,
} from 'generated/graphql';
import { isEmpty } from 'lodash';
import { useEffect } from 'react';
import { useFetchMore } from 'v2/core/repository/edge-server/utils/records';
import { useGetUser } from 'v2/core/services/user';

interface FetchDashboardsOptions {
  query?: string;
  toFetchAll?: boolean;
  shouldFetchOtherUserDashboards?: boolean;
  onError?: (error: Error) => void;
}

export const useFetchDashboards = ({
  query,
  toFetchAll,
  shouldFetchOtherUserDashboards = false,
  onError = () => {},
}: FetchDashboardsOptions = {}) => {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();

  const {
    data,
    loading,
    error,
    fetchMore: eFetchMore,
  } = useListDashboardsQuery({
    variables: {
      tenantID,
      userID,
      filter: {
        query,
        editableBy: shouldFetchOtherUserDashboards ? undefined : userID,
        notEditableBy: shouldFetchOtherUserDashboards ? userID : undefined,
        useCase: DashboardUseCase.Default,
      },
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
    onError,
  });

  const [isLoadingMore, fetchMore] = useFetchMore(
    eFetchMore,
    data?.listDashboards?.nextCursor,
  );

  useEffect(() => {
    const hasMore = data ? !!data.listDashboards?.nextCursor : true;
    if (loading || isLoadingMore || !hasMore || !toFetchAll) {
      return;
    }
    fetchMore();
  }, [data, fetchMore, isLoadingMore, loading, toFetchAll]);

  const { showErrorNotification } = useNotificationContext();

  useEffect(() => {
    if (error) {
      showErrorNotification({
        contentComponent: 'Failed to get Dashboard list',
        props: {},
      });
    }
  }, [error, showErrorNotification]);

  return {
    dashboards: data?.listDashboards?.dashboards as
      | DashboardListItemViewFragment[]
      | undefined,
    loading: !isEmpty(data) ? false : loading || isLoadingMore,
    hasMore: data ? !!data?.listDashboards?.nextCursor : false,
  };
};
